import {
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
} from "chart.js";
import _ from "lodash";
import { Scatter } from "react-chartjs-2";
import { mutate as globalMutate } from "swr";

import annotationPlugin from "chartjs-plugin-annotation";
import { useCallback, useEffect, useRef, useState } from "react";
import { updateAnaerobicThresholdValues } from "../../../api/anaerobic-threshold";
import { updateInbar12Graph } from "../../../api/inbar12";
import { updateWasserman9Graph } from "../../../api/wasserman9";
import { useGraphs } from "../../../context/GraphsProvider";
import useGraphConfig from "../../../hooks/useGraphConfig";
import useTestId from "../../../hooks/useTestId";
import { TestGraph } from "../../../types";
import {
    ChartType,
    RESPONSE_1_DATASET_INDEX,
    RESPONSE_2_DATASET_INDEX,
    RESPONSE_3_DATASET_INDEX,
    TREND_1_DATASET_INDEX,
    TREND_1B_DATASET_INDEX,
    TREND_2_DATASET_INDEX,
    TREND_2B_DATASET_INDEX,
} from "./config";
import GraphLeftSideTitles from "./GraphLeftSideTitles";
import GraphRightSideTitles from "./GraphRightSideTitles";
import StatusSelectPopup from "./StatusSelectPopup";
import { useAuth } from "../../../context/AuthContext";
import { yAxisUnitsPlugin } from "./graphPlugins";
import { twMerge } from "tailwind-merge";
import { clearGraphsCache } from "../../../hooks/useGraphsFactory";
import { isO2PulseGraph } from "../../../utils";

ChartJS.register(
    LineElement,
    PointElement,
    LinearScale,
    Title,
    Legend,
    CategoryScale,
    annotationPlugin,
    Filler
);

export type GraphSize =
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "xxl"
    | "compare-primary"
    | "compare-secondary";

type GraphProps = {
    graphData: TestGraph;
    size: GraphSize;
    onVatRelease?: (vat: number, graph_id?: string) => void;
    onRCPRelease?: (rcp: number) => void;
    withoutMaxPoint?: boolean;
    disabled?: boolean;
    isLeftGraph?: boolean;
    isReverseVATColor?: boolean;
    onChange?: (id: TestGraph["graph_id"], data: Partial<TestGraph>) => void;
};

const Graph = ({
    graphData,
    size = "sm",
    onVatRelease,
    onRCPRelease,
    disabled = false,
    isLeftGraph = false,
    isReverseVATColor = false,
    onChange,
}: GraphProps) => {
    const { isAllowed } = useAuth();
    const {
        mutate,
        refetchAdditionalData,
        graphsType,
        refreshDataWithUnsavedChanges,
    } = useGraphs();
    const isAnaerobic = graphsType === "anaerobic-threshold";
    const isWasserman9 = graphsType === "wasserman-9";

    const allowedToChangePeakStatus =
        (graphsType === "inbar-12" && isAllowed("Inbar12 Peak")) ||
        (graphsType === "wasserman-9" && isAllowed("Wasserman9 Peak"));
    const allowedToChangeLineStatus =
        (graphsType === "wasserman-9" &&
            graphData.slope1_status &&
            isAllowed("Wasserman9 Slope")) ||
        (graphsType === "inbar-12" && isAllowed("Inbar12 Response"));
    const allowedToChangeLimits =
        (graphsType === "anaerobic-threshold" &&
            isAllowed("Anaerobic VAT") &&
            isAllowed("Anaerobic RCP")) ||
        (graphsType === "inbar-12" && isAllowed("Inbar12 Slope")) ||
        (graphsType === "wasserman-9" && isAllowed("Wasserman9 Slope"));

    const mutateGraphData = (
        data: Partial<TestGraph>,
        revalidateGraphs: boolean = false
    ) => {
        globalMutate(
            (key) =>
                typeof key === "string" &&
                key?.toString().startsWith(`graphs:${graphsType}`),
            (cachedGraphs: TestGraph[] | undefined) => {
                if (!cachedGraphs) return cachedGraphs;
                const globalDataKeys: (keyof TestGraph)[] = [
                    "vat_value",
                    "rcp_value",
                ];
                const newGlobalData = _.pick(data, globalDataKeys);
                const newGraphs = cachedGraphs.map((graph) => {
                    if (graph.graph_id === graphData.graph_id) {
                        const updatedGraph = { ...graph, ...data } as TestGraph;
                        return updatedGraph;
                    }
                    const relevantGlobalData = _.pick(
                        newGlobalData,
                        Object.keys(graph)
                    );
                    return { ...graph, ...relevantGlobalData } as TestGraph; // Update global data for all graphs
                });
                return newGraphs;
            },
            revalidateGraphs
        );

        if (onChange) {
            onChange(graphData.graph_id, data);
        }
    };
    const chartRef = useRef(null);
    const testId = useTestId();

    const lowerLimit = graphData.slope_calculation_begin_value;
    const upperLimit = graphData.slope_calculation_end_value;

    const [showGraph1LinePopup, setShowGraph1LinePopup] = useState(false);
    const [showGraph2LinePopup, setShowGraph2LinePopup] = useState(false);
    const [showGraph3LinePopup, setShowGraph3LinePopup] = useState(false);
    const [showGraph1PointPopup, setShowGraph1PointPopup] = useState(false);
    const [showGraph2PointPopup, setShowGraph2PointPopup] = useState(false);
    const [showGraph3PointPopup, setShowGraph3PointPopup] = useState(false);
    const [popupPosition, setPopupPosition] = useState<{
        x: number | null;
        y: number | null;
    }>({ x: null, y: null });

    const draggingVATRef = useRef(false);
    const draggingRCPRef = useRef(false);
    const draggingLowerLimitRef = useRef(false);
    const draggingUpperLimitRef = useRef(false);
    const prevLowerLimitValueRef = useRef<undefined | number>(undefined);
    const prevUpperLimitValueRef = useRef<undefined | number>(undefined);

    const onGraphClick = useCallback(
        (
            e: any,
            elements: any,
            datasets: any[] // Pass datasets as parameter
        ) => {
            if (disabled) return;
            if (!chartRef.current || isAnaerobic) return;
            if (elements.length > 0 && elements[0]) {
                const element = elements[0];
                const { datasetIndex, index: xIndex } = element;
                if (
                    draggingLowerLimitRef.current ||
                    draggingUpperLimitRef.current ||
                    draggingVATRef.current ||
                    draggingRCPRef.current
                ) {
                    return;
                }

                // Get the clicked dataset label
                const clickedDataset = datasets[datasetIndex];
                const datasetLabel = clickedDataset?.label;

                setPopupPosition({ x: e.x, y: e.y });

                // Check if it's a peak click by label
                if (datasetLabel?.startsWith("peak")) {
                    const peakNumber = datasetLabel.split(" ")[1]; // "1", "2", or "3"

                    if (peakNumber === "1" && allowedToChangePeakStatus) {
                        setShowGraph1PointPopup((prev) => !prev);
                        setShowGraph1LinePopup(false);
                        setShowGraph2LinePopup(false);
                        setShowGraph2PointPopup(false);
                        setShowGraph3LinePopup(false);
                        setShowGraph3PointPopup(false);
                    } else if (
                        peakNumber === "2" &&
                        allowedToChangePeakStatus
                    ) {
                        setShowGraph2PointPopup((prev) => !prev);
                        setShowGraph2LinePopup(false);
                        setShowGraph1LinePopup(false);
                        setShowGraph1PointPopup(false);
                        setShowGraph3LinePopup(false);
                        setShowGraph3PointPopup(false);
                    } else if (
                        peakNumber === "3" &&
                        allowedToChangePeakStatus
                    ) {
                        setShowGraph3PointPopup((prev) => !prev);
                        setShowGraph3LinePopup(false);
                        setShowGraph1LinePopup(false);
                        setShowGraph1PointPopup(false);
                        setShowGraph2LinePopup(false);
                        setShowGraph2PointPopup(false);
                    }
                    return; // Exit early for peak clicks
                }

                // Handle response/trend line clicks
                if (
                    datasetIndex === RESPONSE_1_DATASET_INDEX ||
                    datasetIndex === TREND_1_DATASET_INDEX ||
                    datasetIndex === TREND_1B_DATASET_INDEX ||
                    datasetIndex === TREND_2B_DATASET_INDEX
                ) {
                    if (allowedToChangeLineStatus) {
                        setShowGraph1LinePopup((prev) => !prev);
                    }
                    setShowGraph1PointPopup(false);
                } else if (
                    datasetIndex === RESPONSE_2_DATASET_INDEX ||
                    datasetIndex === TREND_2_DATASET_INDEX
                ) {
                    if (allowedToChangeLineStatus) {
                        setShowGraph2LinePopup((prev) => !prev);
                    }
                    setShowGraph2PointPopup(false);
                } else if (datasetIndex === RESPONSE_3_DATASET_INDEX) {
                    if (allowedToChangeLineStatus) {
                        setShowGraph3LinePopup((prev) => !prev);
                    }
                    setShowGraph3PointPopup(false);
                }
            }
        },
        [
            disabled,
            isAnaerobic,
            allowedToChangePeakStatus,
            allowedToChangeLineStatus,
        ]
    );

    const { data, options, isRCP } = useGraphConfig({
        graphData: graphData,
        vat: graphData.vat_value,
        rcp: graphData.rcp_value,
        isAnaerobic,
        isWasserman9,
        size,
        onGraphClick,
        showGraph1PointPopup,
        showGraph2PointPopup,
        showGraph3PointPopup,
        isReverseVATColor,
        disablePeakHover: !allowedToChangePeakStatus,
        disableLineHover: !allowedToChangeLineStatus,
    });

    useEffect(() => {
        if (chartRef.current) {
            const chart = chartRef.current as ChartType;
            chart.update();
            setShowGraph1LinePopup(false);
            setShowGraph2LinePopup(false);
            setShowGraph3LinePopup(false);
            // setShowGraph1PointPopup(false);
            // setShowGraph2PointPopup(false);
        }
    }, [data]);

    // MARK: Mouse Move
    const handleMouseMove = useCallback(
        (event: globalThis.MouseEvent) => {
            const chart = chartRef.current as ChartType | null;
            if (!chart) return;

            const canvasPosition = {
                x: event.offsetX,
                y: event.offsetY,
            };

            const xs = graphData.x?.filter(Boolean);
            const graphLeftLimit = xs?.[0] ?? 0;
            const graphRightLimit = xs?.[xs.length - 1] ?? 100;

            let xValue = chart.scales.x.getValueForPixel(canvasPosition.x);
            xValue =
                xValue < graphLeftLimit
                    ? graphLeftLimit
                    : xValue > graphRightLimit
                    ? graphRightLimit
                    : xValue;

            if (draggingLowerLimitRef.current) {
                if (upperLimit !== undefined && xValue < upperLimit) {
                    mutateGraphData({
                        slope_calculation_begin_value: xValue,
                    });
                }
            } else if (draggingUpperLimitRef.current) {
                if (lowerLimit !== undefined && xValue > lowerLimit) {
                    mutateGraphData({ slope_calculation_end_value: xValue });
                }
            } else if (draggingVATRef.current) {
                if (xValue >= 0) {
                    const vatClosetValue = graphData.x?.reduce(
                        (prev, curr) =>
                            Math.abs(curr - xValue) < Math.abs(prev - xValue)
                                ? curr
                                : prev,
                        graphData.x[0]
                    );

                    const vatClosestIndex = graphData.x?.findIndex(
                        (x) => x === vatClosetValue
                    );
                    mutateGraphData({ vat_value: vatClosestIndex ?? -1 });
                    clearGraphsCache("inbar-12");
                }
            } else if (draggingRCPRef.current) {
                if (xValue >= 0) {
                    const rcpClosetValue = graphData.x?.reduce(
                        (prev, curr) =>
                            Math.abs(curr - xValue) < Math.abs(prev - xValue)
                                ? curr
                                : prev,
                        graphData.x[0]
                    );

                    const rcpClosestIndex = graphData.x?.findIndex(
                        (x) => x === rcpClosetValue
                    );

                    mutateGraphData({ rcp_value: rcpClosestIndex ?? -1 });
                }
            }
        },
        [graphData.x, lowerLimit, upperLimit]
    );

    // MARK: Mouse Down
    const handleMouseDown = useCallback(
        (event: globalThis.MouseEvent) => {
            const chart = chartRef.current as ChartType | null;
            if (disabled || !chart) return;

            const canvasPosition = {
                x: event.offsetX,
                y: event.offsetY,
            };

            const lowerLimit =
                chart.options.plugins.annotation.annotations.lowerLimit;
            const upperLimit =
                chart.options.plugins.annotation.annotations.upperLimit;

            const vat = chart.options.plugins.annotation.annotations.vat;
            const rcp = chart.options.plugins.annotation.annotations.rcp;

            const lowerLimitPixel = chart.scales.x.getPixelForValue(
                lowerLimit?.xMin
            );

            const upperLimitPixel = chart.scales.x.getPixelForValue(
                upperLimit?.xMin
            );

            const vatPixel = chart.scales.x.getPixelForValue(vat?.xMin);
            const rcpPixel = chart.scales.x.getPixelForValue(rcp?.xMin);

            const tolerance = 5; // pixels
            if (
                allowedToChangeLimits &&
                Math.abs(canvasPosition.x - lowerLimitPixel) < tolerance
            ) {
                draggingLowerLimitRef.current = true;
            } else if (
                allowedToChangeLimits &&
                Math.abs(canvasPosition.x - upperLimitPixel) < tolerance
            ) {
                draggingUpperLimitRef.current = true;
            } else if (
                isAllowed("Anaerobic VAT") &&
                Math.abs(canvasPosition.x - vatPixel) < tolerance
            ) {
                draggingVATRef.current = true;
            } else if (
                isAllowed("Anaerobic RCP") &&
                Math.abs(canvasPosition.x - rcpPixel) < tolerance
            ) {
                draggingRCPRef.current = true;
            }
            chart.canvas.addEventListener("mousemove", handleMouseMove);
        },
        [disabled, handleMouseMove]
    );

    // MARK: Mouse Up
    const handleMouseUp = useCallback(async () => {
        try {
            const chart = chartRef.current as ChartType | null;
            if (disabled || !chart) return;

            const params = {
                testId,
                graph_id: graphData.graph_id,
                slope_calculation_begin_value: lowerLimit,
                slope_calculation_end_value: upperLimit,
                peak1_status: graphData.peak1_status,
                peak2_status: graphData.peak2_status,
                response1_status: graphData.response1_status,
                response2_status: graphData.response2_status,
            };

            const onLimitChange = async () => {
                const newGraphData = await (isAnaerobic
                    ? updateAnaerobicThresholdValues(params)
                    : isWasserman9
                    ? updateWasserman9Graph(params)
                    : updateInbar12Graph(params));
                if (!newGraphData) return;

                const newVat = newGraphData.vat_value;
                const newRcp = newGraphData.rcp_value;
                const newSlope = newGraphData.slope_value;
                if (newVat) {
                    mutateGraphData({ vat_value: newVat }, true);
                    refetchAdditionalData?.();
                }
                if (newRcp) {
                    mutateGraphData({ rcp_value: newRcp }, true);
                    refetchAdditionalData?.();
                }
                if (newSlope) {
                    mutateGraphData({
                        slope_calculation_begin_value:
                            newGraphData.slope_calculation_begin_value,
                        slope_calculation_end_value:
                            newGraphData.slope_calculation_end_value,
                        slope1_status: newGraphData.slope1_status,
                        trend1_xy: newGraphData.trend1_xy,
                        trend1b_xy: newGraphData.trend1b_xy,
                        trend2_xy: newGraphData.trend2_xy,
                        trend2b_xy: newGraphData.trend2b_xy,
                    });
                    refetchAdditionalData?.();
                    await refreshDataWithUnsavedChanges({
                        [graphData.graph_id]: {
                            slope1_status: newGraphData.slope1_status,
                        },
                    });
                }
            };

            if (
                (draggingLowerLimitRef.current &&
                    prevLowerLimitValueRef.current !== lowerLimit) ||
                (draggingUpperLimitRef.current &&
                    prevUpperLimitValueRef.current !== upperLimit)
            ) {
                mutateGraphData({
                    slope_calculation_begin_value: lowerLimit,
                    slope_calculation_end_value: upperLimit,
                });
                prevLowerLimitValueRef.current = lowerLimit;
                prevUpperLimitValueRef.current = upperLimit;
                onLimitChange();
            }
            if (
                draggingVATRef.current === true &&
                graphData.vat_value !== undefined &&
                onVatRelease
            ) {
                onVatRelease(graphData.vat_value, graphData.graph_id);
            }
            if (
                draggingRCPRef.current === true &&
                graphData.rcp_value !== undefined &&
                isRCP !== undefined &&
                onRCPRelease
            ) {
                onRCPRelease(graphData.rcp_value);
            }
            chart.canvas.removeEventListener("mousemove", handleMouseMove);
        } catch (e) {
            console.error(e);
            alert("An Error has occured");
        } finally {
            draggingLowerLimitRef.current = false;
            draggingUpperLimitRef.current = false;
            draggingVATRef.current = false;
            draggingRCPRef.current = false;
        }
    }, [
        disabled,
        testId,
        graphData.graph_id,
        graphData.peak1_status,
        graphData.peak2_status,
        graphData.response1_status,
        graphData.response2_status,
        graphData.slope_calculation_begin_value,
        graphData.slope_calculation_end_value,
        lowerLimit,
        upperLimit,
        onVatRelease,
        isRCP,
        onRCPRelease,
        handleMouseMove,
        graphsType,
        graphData.graph_id,
    ]);

    useEffect(() => {
        const chart = chartRef.current as ChartType | null;
        if (!chart) return;
        chart.update();
        // @ts-ignore
        chart.resize();
    }, [size]);

    useEffect(() => {
        const chart = chartRef.current as ChartType | null;
        if (!chart) return;

        const canvas = chart.canvas;
        canvas.addEventListener("mousedown", handleMouseDown);
        canvas.addEventListener("mouseup", handleMouseUp);

        return () => {
            canvas.removeEventListener("mousedown", handleMouseDown);
            canvas.removeEventListener("mouseup", handleMouseUp);
        };
    }, [handleMouseDown, handleMouseUp]);

    const commonStatusSelectPopupProps = {
        top: popupPosition.y,
        left: popupPosition.x,
        size,
        showLeft: isLeftGraph,
    };

    // MARK: Render
    return (
        <div
            className={twMerge(
                `Graph relative cursor-pointer max-h-full bg-white rounded-md pt-2.5 pb-0.5`,
                size !== "xxl" &&
                    size !== "xl" &&
                    (size !== "compare-primary" || isAnaerobic) &&
                    "shadow-[0px_2px_8px_0px_#366CD640]"
            )}
            style={{
                paddingLeft:
                    size === "sm" ? "24px" : size === "xxl" ? "80px" : "40px",
                paddingRight:
                    size === "sm" ? "24px" : size === "xxl" ? "80px" : "40px",
            }}
        >
            {/* Status selectors */}
            <StatusSelectPopup
                type={graphData.slope1_other_statuses ? "slope" : "response"}
                title={graphData.slope_status_title}
                titleLabel={
                    graphData.response1_y_label_no_units ||
                    graphData.response1_y_label
                }
                onSelect={(status) => {
                    const type = graphData.slope1_other_statuses
                        ? "slope"
                        : "response";
                    const newData = {
                        response1_status: type === "slope" ? undefined : status,
                        slope1_status: type === "slope" ? status : undefined,
                    };
                    mutateGraphData(newData);
                }}
                currentStatus={
                    graphData?.[
                        graphData.slope1_other_statuses
                            ? "slope1_status"
                            : "response1_status"
                    ]
                }
                statuses={
                    graphData.slope1_other_statuses ??
                    graphData.response1_other_statuses
                }
                show={showGraph1LinePopup}
                close={() => setShowGraph1LinePopup(false)}
                {...commonStatusSelectPopupProps}
            />
            <StatusSelectPopup
                type={graphData.slope2_other_statuses ? "slope" : "response"}
                titleLabel={
                    graphData?.response2_y_label_no_units ||
                    graphData?.response2_y_label
                }
                onSelect={(status) => {
                    const type = graphData.slope2_other_statuses
                        ? "slope"
                        : "response";
                    const newData = {
                        response2_status: type === "slope" ? undefined : status,
                        slope2_status: type === "slope" ? status : undefined,
                    };
                    mutateGraphData(newData);
                }}
                currentStatus={
                    graphData?.[
                        graphData.slope2_other_statuses
                            ? "slope2_status"
                            : "response2_status"
                    ]
                }
                statuses={
                    graphData.slope2_other_statuses ??
                    graphData.response2_other_statuses
                }
                show={showGraph2LinePopup}
                close={() => setShowGraph2LinePopup(false)}
                {...commonStatusSelectPopupProps}
            />
            {/* Peak status selector */}
            <StatusSelectPopup
                type="peak"
                title={graphData.peak1_status_title}
                titleLabel={
                    graphData.response1_y_label_no_units ||
                    graphData.response1_y_label
                }
                onSelect={(status) => {
                    // change also o2 pulse peak status of other o2 pulse graphs
                    if (isO2PulseGraph(graphData)) {
                        mutate((prev) => {
                            if (!prev) return prev;
                            const newGraphs = prev.map((graph) => {
                                if (isO2PulseGraph(graph)) {
                                    return {
                                        ...graph,
                                        peak1_status: status,
                                    } as TestGraph;
                                }
                                return graph;
                            });
                            return newGraphs;
                        }, false);
                    } else {
                        mutateGraphData({ peak1_status: status });
                    }
                }}
                currentStatus={graphData?.peak1_status}
                statuses={graphData.peak1_other_statuses}
                show={showGraph1PointPopup}
                close={() => setShowGraph1PointPopup(false)}
                {...commonStatusSelectPopupProps}
            />
            <StatusSelectPopup
                type="peak"
                titleLabel={
                    graphData?.response2_y_label_no_units ||
                    graphData?.response2_y_label
                }
                onSelect={(status) => {
                    mutateGraphData({ peak2_status: status });
                }}
                currentStatus={graphData?.peak2_status}
                statuses={graphData.peak2_other_statuses}
                show={showGraph2PointPopup}
                close={() => setShowGraph2PointPopup(false)}
                {...commonStatusSelectPopupProps}
            />
            <StatusSelectPopup
                type="peak"
                titleLabel={graphData?.response3_y_label}
                onSelect={(status) => {
                    mutateGraphData({ peak3_status: status });
                }}
                currentStatus={graphData?.peak3_status}
                statuses={graphData.peak3_other_statuses}
                show={showGraph3PointPopup}
                close={() => setShowGraph3PointPopup(false)}
                {...commonStatusSelectPopupProps}
            />
            <GraphLeftSideTitles size={size} graphData={graphData} />
            <GraphRightSideTitles size={size} graphData={graphData} />
            <div
                className="relative h-full"
                style={{ aspectRatio: options.aspectRatio }}
            >
                <Scatter
                    ref={chartRef}
                    data={data}
                    options={options}
                    plugins={[
                        yAxisUnitsPlugin(
                            "response1_y_label_units" in graphData
                                ? graphData.response1_y_label_units
                                : undefined,
                            "response2_y_label_units" in graphData
                                ? graphData.response2_y_label_units
                                : undefined
                        ),
                    ]}
                    className="z-10"
                />
            </div>
        </div>
    );
};

export default Graph;
